// Pure derivations for the Statistiques page (Statistics.js). Every count the
// page shows is computed by GET /api/statistics/overview (Backend
// statistics_service.get_comprehensive_stats): these helpers only read the
// URL, build the query, reshape series for the charts and format numbers.
// They never recount tickets — a sum here is a sum of the API's own rows.

export const PERIODS = ['7', '30', '90', '365', 'custom'];
export const DEFAULT_PERIOD = '30';
export const VIEWS = ['activity', 'motifs', 'clients', 'ai'];

// URL contract (other pages link here): ?client=<clients_edn id> (B2B client
// page), ?carrier=<name>&days=30 (carrier pages), ?days=7|30|90|365 (clients
// list), ?days=custom&start=&end=, ?brand=<id>, ?view=<tab>.
export const readStatsParams = (searchParams) => {
  const get = (key) => (searchParams.get(key) || '').trim();
  const days = PERIODS.includes(get('days')) ? get('days') : DEFAULT_PERIOD;
  const view = VIEWS.includes(get('view')) ? get('view') : 'activity';
  return {
    brand: get('brand'),
    client: get('client'),
    carrier: get('carrier'),
    days,
    start: get('start'),
    end: get('end'),
    view,
  };
};

// The custom period applies once both dates are set; until then the page
// keeps asking for the default window (what the API answers without dates).
export const customRangeReady = (params) => params.days === 'custom' && Boolean(params.start && params.end);

// Query of GET /api/statistics/overview — same parameters as before the
// rework (brand_id, client_id, carrier, then the window).
export const overviewQuery = (params) => {
  const query = new URLSearchParams();
  if (params.brand) query.append('brand_id', params.brand);
  if (params.client) query.append('client_id', params.client);
  if (params.carrier) query.append('carrier', params.carrier);
  if (customRangeReady(params)) {
    query.append('start_date', params.start);
    query.append('end_date', params.end);
  } else {
    query.append('days', params.days === 'custom' ? DEFAULT_PERIOD : params.days);
  }
  return query.toString();
};

// ---------------------------------------------------------------- dates

const DAY_MS = 24 * 3600 * 1000;

const parseDay = (value) => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const dayKey = (ms) => new Date(ms).toISOString().slice(0, 10);

// Monday of the UTC week holding the day.
const weekStart = (ms) => {
  const weekday = (new Date(ms).getUTCDay() + 6) % 7;
  return ms - weekday * DAY_MS;
};

// The API groups tickets by creation day (UTC) and omits days without a
// ticket: those days are zeros of the same window, filled for the chart only.
export const fillDays = (daily, periodStart, periodEnd) => {
  const rows = Array.isArray(daily) ? daily : [];
  const byDay = new Map(rows.map((row) => [String(row.date).slice(0, 10), row]));
  const first = parseDay(periodStart) ?? parseDay(rows[0]?.date);
  const last = parseDay(periodEnd) ?? parseDay(rows[rows.length - 1]?.date);
  if (first == null || last == null || last < first || (last - first) / DAY_MS > 800) {
    return rows.map((row) => ({ date: String(row.date).slice(0, 10), total: row.total || 0, resolved: row.resolved || 0 }));
  }
  const out = [];
  for (let ms = first; ms <= last; ms += DAY_MS) {
    const key = dayKey(ms);
    const row = byDay.get(key);
    out.push({ date: key, total: row?.total || 0, resolved: row?.resolved || 0 });
  }
  return out;
};

// Day bars up to 3 months, week bars beyond (a year of days is 365 slivers).
export const granularityFor = (days) => (days.length > 92 ? 'week' : 'day');

export const bucketSeries = (days, granularity) => {
  if (granularity !== 'week') return days.map((d) => ({ key: d.date, total: d.total, resolved: d.resolved }));
  const weeks = new Map();
  days.forEach((d) => {
    const key = dayKey(weekStart(parseDay(d.date)));
    const bucket = weeks.get(key) || { key, total: 0, resolved: 0 };
    bucket.total += d.total;
    bucket.resolved += d.resolved;
    weeks.set(key, bucket);
  });
  return [...weeks.values()];
};

// One bulk-import day (thousands of tickets) would flatten every other bar:
// cap the axis near the 95th percentile and name the clipped bars. Tooltips
// keep the true values.
export const outlierCap = (values) => {
  const sorted = values.filter((v) => typeof v === 'number').sort((a, b) => a - b);
  if (!sorted.length) return null;
  const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
  const max = sorted[sorted.length - 1];
  return p95 > 0 && max > p95 * 3 ? Math.ceil(p95 * 1.5) : null;
};

// ---------------------------------------------------------------- rows

// Status rows: the API groups by stored status; « ai_resolved » and « closed »
// are read as « resolved » (constants/ticketStatuses normalize), so their rows
// merge into one line instead of three « Résolu ».
export const mergeStatusRows = (statuses, normalize) => {
  const merged = new Map();
  (statuses || []).forEach((row) => {
    const key = normalize(row.status);
    const entry = merged.get(key) || { status: key, count: 0 };
    entry.count += row.count || 0;
    merged.set(key, entry);
  });
  return [...merged.values()].sort((a, b) => b.count - a.count);
};

// Brands: the API can return several « No Brand » rows (null and unknown ids).
export const mergeBrandRows = (brands, noBrandLabel) => {
  const merged = new Map();
  (brands || []).forEach((b) => {
    const label = !b.brand_name || b.brand_name === 'No Brand' ? noBrandLabel : b.brand_name;
    const entry = merged.get(label) || { label, count: 0, aiResolved: 0 };
    entry.count += b.ticket_count || 0;
    entry.aiResolved += b.ai_resolved || 0;
    merged.set(label, entry);
  });
  return [...merged.values()].sort((a, b) => b.count - a.count);
};

// Keep n rows, sum the tail into one « Autres » row.
export const rollup = (rows, n) => {
  if (!rows || rows.length <= n) return { head: rows || [], tail: null };
  const tail = rows.slice(n);
  return {
    head: rows.slice(0, n),
    tail: { size: tail.length, count: tail.reduce((sum, row) => sum + (row.count || 0), 0) },
  };
};

// Tools-per-ticket histogram: 0..6 tools, then « 7+ ».
export const toolsHistogram = (distribution) => {
  const buckets = [];
  let overflow = 0;
  (distribution || []).forEach((d) => {
    if (d.tools_count <= 6) buckets.push({ label: String(d.tools_count), count: d.ticket_count || 0 });
    else overflow += d.ticket_count || 0;
  });
  if (overflow > 0) buckets.push({ label: '7+', count: overflow });
  return buckets;
};

// Motif × carrier matrix: a motif keeps its hue across filters — hues follow
// the motif names in alphabetical order (series 1..6), « Autres » stays grey.
export const matrixSlots = (matrix) => {
  const names = matrix?.motifs || [];
  const other = matrix?.has_other && names.length ? names[names.length - 1] : null;
  const ordered = names.filter((name) => name !== other).sort((a, b) => a.localeCompare(b, 'fr'));
  const slots = {};
  names.forEach((name) => { slots[name] = name === other ? 'other' : ordered.indexOf(name); });
  return { slots, other };
};

export const matrixValue = (row, name, other) => (name === other ? (row.other || 0) : (row.cells?.[name] || 0));

// A top-client row names the client as the tickets do (metadata.client.name
// or shop_name): find the B2B client page it belongs to, by name or alias.
export const clientIdFor = (name, clients) => {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return null;
  const hit = (clients || []).find((c) => [c.name, ...(c.aliases || [])]
    .some((value) => String(value || '').trim().toLowerCase() === key));
  return hit ? (hit.id || hit._id || null) : null;
};

// ---------------------------------------------------------------- format

const locale = (lang) => (String(lang || 'fr').startsWith('fr') ? 'fr-FR' : lang);

export const formatNumber = (value, lang = 'fr') => (typeof value === 'number' ? value.toLocaleString(locale(lang)) : '—');

export const formatDecimal = (value, lang = 'fr', digits = 1) => (
  typeof value === 'number'
    ? value.toLocaleString(locale(lang), { minimumFractionDigits: 0, maximumFractionDigits: digits })
    : '—'
);

// « 64 % », « 7,1 % » (one decimal below 10).
export const formatPercent = (value, lang = 'fr') => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `${formatDecimal(value >= 10 ? Math.round(value) : value, lang, 1)} %`;
};

// A duration in minutes as { value, unit }: min below an hour, hours below
// two days, days beyond.
export const splitMinutes = (minutes, lang = 'fr') => {
  if (typeof minutes !== 'number' || minutes <= 0) return null;
  if (minutes < 60) return { value: formatNumber(Math.max(1, Math.round(minutes)), lang), unit: 'min' };
  const hours = minutes / 60;
  if (hours < 48) return { value: formatDecimal(hours < 10 ? hours : Math.round(hours), lang, 1), unit: 'h' };
  return { value: formatDecimal(hours / 24, lang, 1), unit: 'j' };
};

export const splitHours = (hours, lang = 'fr') => (typeof hours === 'number' && hours > 0 ? splitMinutes(hours * 60, lang) : null);

export const joinDuration = (split) => (split ? `${split.value} ${split.unit}` : '—');

// Same buckets as the API's sentiment distribution: négatif ≤ 30 < neutre ≤ 60 < positif.
export const sentimentTone = (score) => (score <= 30 ? 'danger' : score <= 60 ? 'warning' : 'success');
